// FILE: src/services/questionFactoryService.js
//
// Data access for the Question Factory pipeline (migration 034).
//
// Layout mirrors badgeAdminService.js — every mutating call is a thin
// wrapper around an admin-gated SECURITY DEFINER RPC; reads also go
// through RPCs since the underlying tables have no row policies.
//
// Phase boundaries:
//   • Phase 1 (this file): CRUD over pipeline_runs / items / events; the
//     "do the work" functions for Stages 2–7 throw with a clear Phase-2
//     marker so consumers fail fast rather than silently no-op.
//   • Phase 2: replace the throw stubs with edge-function calls.
//
// All RPCs return either a single row, an array of rows, or a jsonb object;
// errors surface as plain Error with the Supabase message preserved.

import { supabase } from './supabaseClient';
import { listProviders, getProvider } from './questionFactory/providers';
import { fetchAllPaginated } from './questionService';

// ──────────────────────────────────────────────────────────────────────
// Pipeline runs
// ──────────────────────────────────────────────────────────────────────

/**
 * Create a new pipeline run for a given exam + source.
 *
 * @param {object} args
 * @param {string} args.examId         - 'pmp' | 'pmi-acp' | 'cpmai'
 * @param {string} args.source         - provider id (claude | chatgpt | pmi_infinity | gemini | manual_import)
 * @param {string} [args.domain]       - optional domain hint (e.g. 'Planning')
 * @param {string} [args.providerModel]- explicit model (overrides default)
 * @param {number} [args.requestedCount=0]
 * @param {object} [args.options]      - free-form provider knobs
 * @returns {Promise<object>}            new pipeline_runs row
 */
export async function createPipelineRun({
  examId,
  source,
  domain         = null,
  providerModel  = null,
  requestedCount = 0,
  options        = {},
} = {}) {
  if (!examId)  throw new Error('createPipelineRun: examId is required');
  if (!source)  throw new Error('createPipelineRun: source is required');

  const { data, error } = await supabase.rpc('create_pipeline_run', {
    p_exam_id:         examId,
    p_source:          source,
    p_domain:          domain,
    p_provider_model:  providerModel,
    p_requested_count: requestedCount,
    p_options:         options,
  });
  if (error) throw new Error(`createPipelineRun: ${error.message}`);
  return data;
}

/**
 * List pipeline runs, newest first, optionally filtered by exam.
 *
 * @param {object} [args]
 * @param {string} [args.examId]
 * @param {number} [args.limit=50]
 * @param {number} [args.offset=0]
 * @returns {Promise<Array>}
 */
export async function listPipelineRuns({ examId = null, limit = 50, offset = 0 } = {}) {
  const { data, error } = await supabase.rpc('list_pipeline_runs', {
    p_exam_id: examId,
    p_limit:   limit,
    p_offset:  offset,
  });
  if (error) throw new Error(`listPipelineRuns: ${error.message}`);
  return data || [];
}

export async function getPipelineRun(runId) {
  if (!runId) throw new Error('getPipelineRun: runId is required');
  const { data, error } = await supabase.rpc('get_pipeline_run', { p_run_id: runId });
  if (error) throw new Error(`getPipelineRun: ${error.message}`);
  return data;
}

/**
 * Pause / resume / cancel / complete / fail a run.
 *
 * @param {string} runId
 * @param {string} status - 'queued'|'running'|'paused'|'completed'|'cancelled'|'failed'
 * @param {string} [note] - free-form note (stored on the audit event; copies into error_message when status='failed')
 */
export async function updatePipelineRunStatus(runId, status, note = null) {
  const { data, error } = await supabase.rpc('update_pipeline_run_status', {
    p_run_id: runId,
    p_status: status,
    p_note:   note,
  });
  if (error) throw new Error(`updatePipelineRunStatus: ${error.message}`);
  return data;
}

// ──────────────────────────────────────────────────────────────────────
// Pipeline items
// ──────────────────────────────────────────────────────────────────────

/**
 * Append a draft to a run. Increments runs.generated_count and logs a
 * 'stage_entered' event automatically.
 */
export async function addPipelineItem({ runId, draftId, rawPayload } = {}) {
  if (!runId)      throw new Error('addPipelineItem: runId is required');
  if (!rawPayload) throw new Error('addPipelineItem: rawPayload is required');
  const { data, error } = await supabase.rpc('add_pipeline_item', {
    p_run_id:      runId,
    p_draft_id:    draftId || null,
    p_raw_payload: rawPayload,
  });
  if (error) throw new Error(`addPipelineItem: ${error.message}`);
  return data;
}

export async function listPipelineItems({ runId, status = null, limit = 200, offset = 0 } = {}) {
  if (!runId) throw new Error('listPipelineItems: runId is required');
  const { data, error } = await supabase.rpc('list_pipeline_items', {
    p_run_id: runId,
    p_status: status,
    p_limit:  limit,
    p_offset: offset,
  });
  if (error) throw new Error(`listPipelineItems: ${error.message}`);
  return data || [];
}

export async function listPipelineEvents({ runId, limit = 200 } = {}) {
  if (!runId) throw new Error('listPipelineEvents: runId is required');
  const { data, error } = await supabase.rpc('list_pipeline_events', {
    p_run_id: runId,
    p_limit:  limit,
  });
  if (error) throw new Error(`listPipelineEvents: ${error.message}`);
  return data || [];
}

// ──────────────────────────────────────────────────────────────────────
// Dashboard
// ──────────────────────────────────────────────────────────────────────

export async function getDashboard() {
  const { data, error } = await supabase.rpc('question_factory_dashboard');
  if (error) throw new Error(`getDashboard: ${error.message}`);
  return data || {};
}

// ──────────────────────────────────────────────────────────────────────
// Live bank inventory (drives gap-targeted generation)
// ──────────────────────────────────────────────────────────────────────

/**
 * Active-question inventory across the whole bank, grouped by cert and by
 * category. "Active" = the live, learner-facing pool (status='active').
 * Drafts, retired, and new/unapproved rows do not count toward "stocked".
 *
 * @returns {Promise<Object<string, { total: number, byCategory: Object<string, number> }>>}
 */
export async function getActiveInventory() {
  const { data, error } = await fetchAllPaginated(
    () =>
      supabase
        .from('questions')
        .select('exam_id, category')
        .eq('status', 'active')
        .order('id', { ascending: true }),
    'getActiveInventory',
  );
  if (error) throw new Error(`getActiveInventory: ${error.message}`);

  const out = {};
  for (const row of data || []) {
    const examId = row.exam_id;
    if (!examId) continue;
    if (!out[examId]) out[examId] = { total: 0, byCategory: {} };
    out[examId].total += 1;
    const cat = (row.category || '').trim() || '(uncategorized)';
    out[examId].byCategory[cat] = (out[examId].byCategory[cat] || 0) + 1;
  }
  return out;
}

export function aggregateThroughput(runs, days = 30) {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const byExam = {};
  for (const r of runs || []) {
    const ts = r && r.created_at ? new Date(r.created_at).getTime() : 0;
    if (!ts || ts < cutoff) continue;
    if (!r.exam_id) continue;
    byExam[r.exam_id] = (byExam[r.exam_id] || 0) + (r.published_count || 0);
  }
  const out = {};
  for (const [id, published] of Object.entries(byExam)) {
    out[id] = { published, perDay: published / days };
  }
  return out;
}

/**
 * Computes recent publish throughput per exam from the pipeline-run history.
 *
 * Sums `published_count` across runs created within the trailing `days` window
 * and divides by the window to get a published-questions-per-day rate. Used by
 * the Domain Coverage Heatmap to estimate when under-target domains will fill.
 *
 * Best-effort: returns {} on any error so callers can degrade gracefully.
 *
 * @param {object} [args]
 * @param {number} [args.days=30]    trailing window in days
 * @param {number} [args.lookback=1000] max runs to scan (covers the window)
 * @returns {Promise<Object<string,{published:number, perDay:number}>>}
 */
export async function getPublishThroughput({ days = 30, lookback = 1000 } = {}) {
  try {
    const runs = await listPipelineRuns({ limit: lookback });
    return aggregateThroughput(runs, days);
  } catch (err) {
    console.warn('[questionFactoryService] getPublishThroughput failed:', err);
    return {};
  }
}

// ──────────────────────────────────────────────────────────────────────
// Providers (read-only registry exposed to the UI)
// ──────────────────────────────────────────────────────────────────────

/** Provider list for picker UI (id, displayName, enabled, comingSoon, cliOnly). */
export function listAvailableProviders() {
  return listProviders().map((p) => ({
    id:             p.id,
    displayName:    p.displayName,
    enabled:        p.enabled,
    comingSoon:     Boolean(p.comingSoon),
    cliOnly:        Boolean(p.cliOnly),
    cliNote:        p.cliNote || null,
    supportedModes: p.supportedModes,
    defaultModel:   p.defaultModel,
  }));
}

export function getProviderDefinition(id) {
  return getProvider(id);
}

// ──────────────────────────────────────────────────────────────────────
// Phase 2 / 3 surfaces — stubbed
// ──────────────────────────────────────────────────────────────────────

/**
 * Stage 1 generation. Dispatches to the named provider's adapter
 * (Phase 2a: only ChatGPT is enabled; Phase 2b adds Claude + Infinity).
 *
 * @param {object} args
 * @param {string} args.runId       - pipeline_runs id (pre-created)
 * @param {string} args.provider    - 'chatgpt' | 'claude' | 'pmi_infinity'
 * @param {string} args.examId
 * @param {number} args.count
 * @param {string} [args.domain]
 * @param {string} [args.model]
 * @param {string} [args.difficulty]
 * @param {string} [args.extraInstructions]
 * @returns {Promise<object>} The provider's GenerateResult summary.
 */
export async function runGeneration(args = {}) {
  const { runId, provider, ...rest } = args;
  if (!runId)    throw new Error('runGeneration: runId is required');
  if (!provider) throw new Error('runGeneration: provider is required');
  const def = getProvider(provider);
  if (!def) throw new Error(`runGeneration: unknown provider "${provider}"`);
  if (!def.enabled) {
    throw new Error(`runGeneration: provider "${provider}" is not enabled in this phase.`);
  }
  return def.generate({ runId, ...rest });
}

/** Phase 2 — run Stage 2 structural validation on an item. */
export async function runValidation(/* { itemId } */) {
  throw new Error('runValidation is implemented in Question Factory Phase 2.');
}

/** Phase 2 — request an AI adjudication review for an item. */
export async function runAdjudication(/* { itemId, reviewer } */) {
  throw new Error('runAdjudication is implemented in Question Factory Phase 2.');
}

/** Phase 3 — auto-tag canonical metadata using AI. */
export async function runAutoTag(/* { itemId } */) {
  throw new Error('runAutoTag is implemented in Question Factory Phase 3.');
}

/** Phase 3 — compute duplicate similarity against the existing bank. */
export async function runDuplicateCheck(/* { itemId } */) {
  throw new Error('runDuplicateCheck is implemented in Question Factory Phase 3.');
}

/**
 * Phase 3 — promote a staged/adjudicated item into the live questions
 * table via the publish_pipeline_item RPC. Returns { item_id, question_id, status }.
 */
export async function publishItem({ itemId, note = null } = {}) {
  if (!itemId) throw new Error('publishItem: itemId is required');
  const { data, error } = await supabase.rpc('publish_pipeline_item', {
    p_item_id: itemId,
    p_admin_note: note,
  });
  if (error) throw new Error(`publishItem: ${error.message}`);
  return data;
}

/** Phase 3 — admin reject. */
export async function rejectItem({ itemId, note = null } = {}) {
  if (!itemId) throw new Error('rejectItem: itemId is required');
  const { data, error } = await supabase.rpc('reject_pipeline_item', {
    p_item_id: itemId,
    p_admin_note: note,
  });
  if (error) throw new Error(`rejectItem: ${error.message}`);
  return data;
}

/** Phase 3 — admin edit (replace raw_payload). */
export async function editItem({ itemId, payload, note = null } = {}) {
  if (!itemId) throw new Error('editItem: itemId is required');
  if (!payload || typeof payload !== 'object') {
    throw new Error('editItem: payload must be an object');
  }
  const { data, error } = await supabase.rpc('edit_pipeline_item', {
    p_item_id: itemId,
    p_new_payload: payload,
    p_admin_note: note,
  });
  if (error) throw new Error(`editItem: ${error.message}`);
  return data;
}

/** Phase 3 — per-cert auto-publish settings. */
export async function getAutoPublishSettings() {
  const { data, error } = await supabase.rpc('get_auto_publish_settings');
  if (error) throw new Error(`getAutoPublishSettings: ${error.message}`);
  return Array.isArray(data) ? data : [];
}

export async function setAutoPublishSetting({ examId, enabled } = {}) {
  if (!examId) throw new Error('setAutoPublishSetting: examId is required');
  const { data, error } = await supabase.rpc('set_auto_publish_setting', {
    p_exam_id: examId,
    p_enabled: !!enabled,
  });
  if (error) throw new Error(`setAutoPublishSetting: ${error.message}`);
  return data;
}

// ──────────────────────────────────────────────────────────────────────
// Demand-aware generation targeting (migration 107)
// ──────────────────────────────────────────────────────────────────────

/**
 * Everything the admin Generation panel renders: per-cert bank, baseline
 * target, demand signals, unresolved queue state, calculated target,
 * eligibility, blocking reason, priority and blueprint deficits.
 *
 * This is the SAME view the qf-overnight workflow consults through
 * qf_next_generation_target(), so the panel cannot disagree with what the
 * generator actually does. Nothing is re-derived client-side.
 *
 * @returns {Promise<{evaluated_at: string, config: Object, certs: Array}>}
 */
export async function getGenerationDashboard() {
  const { data, error } = await supabase.rpc('qf_generation_dashboard');
  if (error) throw new Error(`getGenerationDashboard: ${error.message}`);
  return data || {};
}

/**
 * Answer-length tell metrics (migration 108's qf_length_tell_monitor, exposed by
 * migration 111). The view is REVOKEd from authenticated, so this must go
 * through the admin-gated RPC rather than selecting the view directly.
 */
export async function getLengthTellSummary() {
  const { data, error } = await supabase.rpc('qf_length_tell_summary');
  if (error) throw new Error(`getLengthTellSummary: ${error.message}`);
  return data || {};
}

/**
 * Per-cert generation switch. Independent of auto-publish: a cert can be
 * allowed to generate while still requiring admin approval to publish, or
 * vice versa. Default OFF for every cert.
 *
 * @param {object} args
 * @param {string} args.examId
 * @param {boolean} args.enabled
 * @returns {Promise<object>} updated question_factory_settings row
 */
export async function setGenerationEnabled({ examId, enabled } = {}) {
  if (!examId) throw new Error('setGenerationEnabled: examId is required');
  const { data, error } = await supabase.rpc('set_generation_enabled', {
    p_exam_id: examId,
    p_enabled: !!enabled,
  });
  if (error) throw new Error(`setGenerationEnabled: ${error.message}`);
  return data;
}

const questionFactoryServiceModule = {
  createPipelineRun,
  listPipelineRuns,
  getPipelineRun,
  updatePipelineRunStatus,
  addPipelineItem,
  listPipelineItems,
  listPipelineEvents,
  getDashboard,
  getActiveInventory,
  listAvailableProviders,
  getProviderDefinition,
  // Phase 2+
  runGeneration,
  runValidation,
  runAdjudication,
  runAutoTag,
  runDuplicateCheck,
  // Phase 3
  publishItem,
  rejectItem,
  editItem,
  getAutoPublishSettings,
  setAutoPublishSetting,
  // Migration 107 — demand-aware generation
  getGenerationDashboard,
  getLengthTellSummary,
  setGenerationEnabled,
};

export default questionFactoryServiceModule;
